import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Controller, useForm } from 'react-hook-form'

import { ArrowUpRightIcon, CheckIcon, PlusIcon, SparklesIcon, XIcon } from 'lucide-react'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'

import {
  ALL_PRODUCT_TYPES,
  METRIC_SOLUTION_LABELS,
  MetricSolution,
  TARGET_ROLE_NAMES,
  type MetricSolution as MetricSolutionType,
} from '@repo/types'
import { Button, Card, Input } from '@repo/ui'
import { NARROW_COLUMN_WIDTH } from 'config'
import { CompanyAvatar, StatusChip } from 'components/display'
import { QueryErrorState } from 'components/feedback'
import { FormSubmitButton } from 'components/input/FormSubmitButton'
import { SelectField } from 'components/input/fields/SelectField'
import { Header } from 'components/navigation'
import { MARKET_ITEM_STATUSES, MARKET_STAT_STATUSES } from 'constants/statuses'
import {
  useSetMarketItemTags,
  useApprovedClaimsQuery,
  useApproveMarketItemQuote,
  useConfirmMarketItemCompany,
  useDismissMarketItemCompany,
  useDismissMarketItemQuote,
  useExtractClaims,
  useMarketItemQuery,
  useProposedClaimsQuery,
  useRejectedClaimsQuery,
  useUpdateMarketItemOutreach,
} from 'hooks/api'
import type { IMarketItemCompanyRef } from 'services/marketItemsApi'
import { dateOnly, getErrorMessage } from 'utils'
import { ROUTES } from 'routes'

import { InternalMarketContentPageSkeleton } from './InternalMarketContentPageSkeleton'
import { AssignMarketContentCompaniesModal } from './AssignMarketContentCompaniesModal'

/**
 * One piece of content: its problem/solution outreach copy, and the companies it
 * is assigned to (VRG-537, epic VRG-445).
 *
 * ## Why this is the first detail surface in the epic
 *
 * Every other market-data surface (`MarketContentTab`, sources, the stats queue) is
 * a table with inline row actions — a status change, a flag toggle, nothing that
 * needs more room than a dropdown item. Problem/solution are multi-line free text
 * and a company assignment is a multi-select, neither of which belongs in a table
 * cell (see the task file's Gap section) — hence a real page rather than a modal.
 *
 * ## Route shape — see routes.ts for why this is `/internal/market/:id`, not the
 * task file's literal `/internal/market/content/:id` (the breadcrumb dead-link
 * reasoning `INTERNAL_ANNOUNCEMENT_EDIT` already documents).
 *
 * ## Save is scoped to problem/solution ONLY
 *
 * The metadata card (URL, source, publication, status, dates) is read-only here —
 * those are set by extraction/row actions on the Content tab, and duplicating their
 * edit surface here was not asked for and would be a second place for them to
 * drift. Company assignment has its OWN save (the modal below), separate from the
 * problem/solution form's — they are different resources with different mutations,
 * and coupling them would mean a company-only change forces a problem/solution
 * PATCH alongside it for no reason.
 */

interface OutreachFormValues {
  problemStatement: string
  solutionStatement: string
}

export const InternalMarketContentPage = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { data: item, isLoading, isError, error, refetch } = useMarketItemQuery(id)
  const updateOutreach = useUpdateMarketItemOutreach()
  const [companiesModalOpen, setCompaniesModalOpen] = useState(false)

  /*
    Stats for THIS piece of content.
    Filtered client-side from the three queue lists rather than adding a
    `GET market-stats?itemId=` endpoint: the lists are already loaded and capped at a
    couple of hundred rows, so a fourth request would buy nothing. If the corpus grows
    past those caps this needs a real filtered endpoint — the caps are the tell.
  */
  const { data: proposedStats = [] } = useProposedClaimsQuery()
  const { data: approvedStats = [] } = useApprovedClaimsQuery()
  const { data: rejectedStats = [] } = useRejectedClaimsQuery()
  const extractStats = useExtractClaims()
  const [extractNote, setExtractNote] = useState<string | null>(null)

  const setTags = useSetMarketItemTags()

  /*
    Options built from the SHARED vocabularies, never retyped here (§44's argument one
    layer down): the solution labels are the marketing site's own customer-facing strings,
    and re-typing them would put a second wording in the product that drifts on the first
    rename. Roles are humanised from the enum value rather than given a hand-written map,
    so a role added upstream appears with a readable label and no edit here.
  */
  const SOLUTION_OPTIONS = useMemo(
    () => [
      { value: '', label: 'Not tagged' },
      ...Object.values(MetricSolution).map(v => ({ value: v, label: METRIC_SOLUTION_LABELS[v] })),
    ],
    [],
  )
  const INDUSTRY_OPTIONS = useMemo(() => ALL_PRODUCT_TYPES.map(v => ({ value: v, label: v })), [])
  const ROLE_OPTIONS = useMemo(
    () =>
      TARGET_ROLE_NAMES.map(v => ({
        value: v,
        label: v.replace(/^company_/, '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()),
      })),
    [],
  )

  const saveTags = async (payload: {
    solution?: string | null
    categories?: string[] | null
    targetRoles?: string[] | null
  }) => {
    if (!id) return
    try {
      await setTags.mutateAsync({ id, ...payload })
    } catch (err) {
      setSaveError(getErrorMessage(err))
    }
  }

  const confirmCompany = useConfirmMarketItemCompany()
  const dismissCompany = useDismissMarketItemCompany()
  const approveQuote = useApproveMarketItemQuote()
  const dismissQuote = useDismissMarketItemQuote()

  /*
    A per-ROW busy id rather than the mutation's own `isPending`.

    The four hooks are shared across every row in their card, so `isPending` is true for
    ALL of them the moment one fires — every button in the list would spin at once and it
    would be unclear which action was actually running. Tracking the link/quote id scopes
    the spinner to the row the user clicked.
  */
  const [companyBusyId, setCompanyBusyId] = useState<string | null>(null)
  const [quoteBusyId, setQuoteBusyId] = useState<string | null>(null)

  const confirmedCompanies = useMemo(
    () => (item?.companies ?? []).filter(c => c.state === 'confirmed'),
    [item?.companies],
  )
  const suggestedCompanies = useMemo(
    () =>
      (item?.companies ?? [])
        .filter(c => c.state === 'suggested')
        // Brands already on file lead. They are the rare case (0 of 20 on the article
        // that prompted this design) and the one a reviewer most wants to act on, so
        // burying them alphabetically among prospects is the wrong default.
        .sort((a, b) => {
          const onFile = Number(Boolean(b.companyId)) - Number(Boolean(a.companyId))
          return onFile !== 0 ? onFile : a.name.localeCompare(b.name)
        }),
    [item?.companies],
  )

  /** Sentinel for the busy id, so "dismiss all" greys every chip rather than none. */
  const ALL_SUGGESTED = '__all_suggested__'

  /**
   * Sequential, not `Promise.all`. Each call returns the whole item and writes it to the
   * detail cache, so firing twenty concurrently would race twenty responses into one
   * cache key and leave whichever landed last — which is not necessarily the one with all
   * twenty removed. Twenty sequential deletes is a second of work, once.
   */
  const dismissAllSuggested = async () => {
    if (!id) return
    setCompanyBusyId(ALL_SUGGESTED)
    try {
      for (const c of suggestedCompanies) {
        await dismissCompany.mutateAsync({ id, childId: c.linkId })
      }
    } catch (err) {
      setSaveError(getErrorMessage(err))
    } finally {
      setCompanyBusyId(null)
    }
  }

  /** The marketing-site label for the solution slug — never the raw slug in the UI. */
  const solutionLabel = item?.solution
    ? (METRIC_SOLUTION_LABELS[item.solution as MetricSolutionType] ?? item.solution)
    : null

  const runCompanyAction = async (action: 'confirm' | 'dismiss', linkId: string) => {
    if (!id) return
    setCompanyBusyId(linkId)
    try {
      const mutation = action === 'confirm' ? confirmCompany : dismissCompany
      await mutation.mutateAsync({ id, childId: linkId })
    } catch (err) {
      setSaveError(getErrorMessage(err))
    } finally {
      setCompanyBusyId(null)
    }
  }

  const runQuoteAction = async (action: 'approve' | 'dismiss', quoteId: string) => {
    if (!id) return
    setQuoteBusyId(quoteId)
    try {
      const mutation = action === 'approve' ? approveQuote : dismissQuote
      await mutation.mutateAsync({ id, childId: quoteId })
    } catch (err) {
      setSaveError(getErrorMessage(err))
    } finally {
      setQuoteBusyId(null)
    }
  }

  const itemStats = useMemo(
    () =>
      [...proposedStats, ...approvedStats, ...rejectedStats].filter(
        stat => stat.sourceItemId === id,
      ),
    [proposedStats, approvedStats, rejectedStats, id],
  )

  const runExtract = async () => {
    setExtractNote(null)
    try {
      const result = await extractStats.mutateAsync(id!)
      setExtractNote(
        result.skipped
          ? `Nothing to extract (${result.skipped.replace('_', ' ')}).`
          : `${result.proposed} proposed for review` +
            (result.rejectedByVerification
              ? `, ${result.rejectedByVerification} discarded as unverifiable`
              : '') +
            '.',
      )
    } catch (e) {
      setExtractNote(getErrorMessage(e))
    }
  }
  const [saveError, setSaveError] = useState<string | null>(null)

  const { control, handleSubmit, reset } = useForm<OutreachFormValues>({
    defaultValues: { problemStatement: '', solutionStatement: '' },
  })

  /**
   * Seed with `reset(record)`, NOT a run of `setValue` calls (§54 — this exact
   * defect shipped twice already in this repo). Keyed on `item.id` rather than the
   * whole object so a background refetch mid-edit does not clobber what the
   * staffer is typing.
   */
  useEffect(() => {
    if (!item) return
    reset({
      problemStatement: item.problemStatement ?? '',
      solutionStatement: item.solutionStatement ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id])

  const close = () => navigate(ROUTES.INTERNAL_MARKET)

  const onSubmit = handleSubmit(async values => {
    if (!id) return
    setSaveError(null)
    try {
      const trimmedProblem = values.problemStatement.trim()
      const trimmedSolution = values.solutionStatement.trim()
      await updateOutreach.mutateAsync({
        id,
        problemStatement: trimmedProblem || null,
        solutionStatement: trimmedSolution || null,
      })
      // Move the isDirty baseline to what was just saved, rather than waiting on
      // the query invalidation to round-trip back through the seeding effect above
      // (which is keyed on item.id and would not re-fire on a data-only refetch).
      reset({ problemStatement: trimmedProblem, solutionStatement: trimmedSolution })
    } catch (e) {
      setSaveError(getErrorMessage(e))
    }
  })

  if (isError) {
    return (
      <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
        {/* "Content" = this item, not the parent page; see the skeleton's VRG-738 note. */}
        <Header title="Content" breadcrumbsOveride="Content" />
        <QueryErrorState
          error={error}
          subject="that content"
          permissionLabel="View the market-data corpus"
          onRetry={() => refetch()}
        />
      </Box>
    )
  }

  if (isLoading || !item) {
    return <InternalMarketContentPageSkeleton />
  }

  let origin: string | null = null
  try {
    origin = new URL(item.url).origin
  } catch {
    origin = null
  }

  return (
    <Box sx={{ maxWidth: NARROW_COLUMN_WIDTH, mx: 'auto' }}>
      <Header
        title={item.title || item.url}
        breadcrumbsOveride={item.title || item.url}
        rightComponents={
          <>
            {/* Enabled always — this is the exit affordance, not a discard (§54). */}
            <Button variant="secondary" onClick={close} disabled={updateOutreach.isPending}>
              Cancel
            </Button>
            <FormSubmitButton
              control={control}
              variant="primary"
              onClick={onSubmit}
              loading={updateOutreach.isPending}
            >
              Save
            </FormSubmitButton>
          </>
        }
      />

      <Stack spacing={4}>
        <Card title="Content" spacing={4}>
          <Stack spacing={4}>
            <Stack
              component="a"
              href={item.url}
              target="_blank"
              rel="noreferrer"
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ color: 'inherit', textDecoration: 'none' }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {item.title || item.url}
              </Typography>
              <ArrowUpRightIcon strokeWidth={1} size={16} style={{ flexShrink: 0, opacity: 0.6 }} />
            </Stack>

            <Stack direction="row" spacing={6} flexWrap="wrap">
              <Field label="Source">{item.sourceName ?? 'Manual'}</Field>
              <Field label="Publication">
                {item.publication ? (
                  origin ? (
                    <Stack
                      component="a"
                      href={origin}
                      target="_blank"
                      rel="noreferrer"
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      sx={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Typography variant="body3" noWrap>
                        {item.publication}
                      </Typography>
                      <ArrowUpRightIcon strokeWidth={1} size={14} style={{ opacity: 0.6 }} />
                    </Stack>
                  ) : (
                    item.publication
                  )
                ) : (
                  '—'
                )}
              </Field>
              <Field label="Published">{item.publishedAt ? dateOnly(item.publishedAt) : '—'}</Field>
              <Field label="Status">
                <StatusChip statuses={MARKET_ITEM_STATUSES} status={item.status} size="small" />
              </Field>
            </Stack>
          </Stack>
        </Card>

        {/* AC1 — the problem this content evidences and what Vera does about it.
            Kept as two SEPARATE fields (see MarketItemsService's comment on
            `problem_statement`/`solution_statement`): the problem is reused across
            every brand this gets assigned to, the solution is brand-specific pitch
            framing. */}
        <Card title="Outreach" spacing={4}>
          <Stack spacing={4}>
            <Controller
              name="problemStatement"
              control={control}
              render={({ field }) => (
                <Input
                  label="Problem"
                  placeholder="Resale buyers cite authentication as the top barrier."
                  helperText="The problem this content evidences, in the words we'd use to a brand. Reused across every company this is assigned to."
                  multiline
                  minRows={4}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <Controller
              name="solutionStatement"
              control={control}
              render={({ field }) => (
                <Input
                  label="Solution"
                  placeholder="What Vera does about it."
                  helperText="Kept separate from Problem — this is the pitch, and it's expected to vary by brand even when the problem is shared."
                  multiline
                  minRows={4}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </Stack>
          {saveError && (
            <Typography variant="body3" sx={{ color: 'error.main', lineHeight: 1.5, mt: 3 }}>
              {saveError}
            </Typography>
          )}
        </Card>

        {/*
          VRG-538 AC1 — the three ARTICLE-level targeting dimensions, editable.

          They were read-only, on the reasoning that correcting one meant re-extracting.
          That was wrong in the direction that matters: extraction proposes a solution from
          six options and a product type from sixty-five, and the whole value of the tag is that
          it resolves to a marketing-site page a salesperson can send. A wrong tag sends the
          wrong page, and "re-run a billed model call" is not a correction mechanism.

          Applied on change, no Save. §43's test: these are single-value pickers with no
          invalid intermediate state, so a commit point would only create the failure where
          you pick and walk away. The Company/ROI dimensions live elsewhere — companies
          below, ROI metrics on the claim, because those are per-claim evidence.
        */}
        <Card title="Targeting" spacing={4}>
          <Stack spacing={4}>
            <SelectField
              label="Solution"
              placeholder="Not tagged"
              value={item.solution ?? ''}
              options={SOLUTION_OPTIONS}
              onChange={v => saveTags({ solution: (v as string) || null })}
            />
            <SelectField
              label="Industry"
              placeholder="Not tagged"
              multiple
              search
              value={item.categories}
              options={INDUSTRY_OPTIONS}
              onChange={v => saveTags({ categories: v as string[] })}
            />
            <SelectField
              label="Role"
              placeholder="Not tagged"
              multiple
              search
              value={item.targetRoles}
              options={ROLE_OPTIONS}
              onChange={v => saveTags({ targetRoles: v as string[] })}
            />
            <Typography variant="body4" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              A tagged solution or industry resolves to a real marketing-site page, which is
              what makes it sendable. Company and ROI-metric targeting are set below and on
              each claim.
            </Typography>
          </Stack>
        </Card>

        {/*
          VRG-573 — CONFIRMED assignments and model SUGGESTIONS in one card, visibly apart.

          Two sections rather than one list with a badge, because they answer different
          questions and demand different things of the reader. Confirmed is "who this
          content is for" — reference material, read at a glance. Suggested is a QUEUE:
          every row wants a decision, and burying those rows among settled ones makes a
          pending decision look like a fact. The suggested section is also what keeps AC4
          honest for a human — outreach filters on `state` in code, but a reviewer needs
          to SEE that a guess is a guess.
        */}
        <Card
          title="Companies"
          spacing={4}
          action={
            <Button
              variant="secondary"
              startIcon={<PlusIcon strokeWidth={1} size={16} />}
              onClick={() => setCompaniesModalOpen(true)}
            >
              Add companies
            </Button>
          }
        >
          {item.companies.length === 0 ? (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Not assigned to any company yet. One piece of content can be relevant to several
              brands. Assign every brand this is useful outreach evidence for.
            </Typography>
          ) : (
            <Stack spacing={5}>
              {confirmedCompanies.length > 0 && (
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  {confirmedCompanies.map(company => (
                    <CompanyChip
                      key={company.linkId}
                      company={company}
                      busy={companyBusyId === company.linkId}
                      onDismiss={() => runCompanyAction('dismiss', company.linkId)}
                    />
                  ))}
                </Stack>
              )}

              {suggestedCompanies.length > 0 && (
                <Stack spacing={3}>
                  <Box>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3}>
                      <Typography variant="body4" color="text.secondary" sx={{ display: 'block' }}>
                        Suggested by extraction · {suggestedCompanies.length}
                      </Typography>
                      {/*
                        Dismiss-all, and deliberately NO confirm-all.

                        The two directions are not symmetric. Dismissing is cheap and
                        reversible — the model re-proposes on the next extraction run — so
                        clearing twenty passing mentions in one click costs nothing if it
                        was wrong. Confirming is what reaches outreach, and a bulk control
                        there would let someone accept twenty brands without reading one,
                        which is the entire gate this list exists to be.
                      */}
                      <Button
                        variant="secondary"
                        destructive="hover"
                        onClick={dismissAllSuggested}
                        loading={companyBusyId === ALL_SUGGESTED}
                      >
                        Dismiss all
                      </Button>
                    </Stack>
                    <Typography
                      variant="body4"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1, lineHeight: 1.5 }}
                    >
                      Proposed from the article text, not yet assigned. Nothing here is used for
                      outreach until you confirm it. Ticked brands we already have on file are
                      shown first.
                    </Typography>
                  </Box>
                  {/*
                    CHIPS that wrap, not a row per brand.

                    One article produced twenty mentions and they rendered as twenty
                    full-width rows — a column of near-identical lines that has to be
                    scanned top to bottom, each repeating "Not a company on file". That
                    label was on every row, which is the tell: when 20 of 20 say the same
                    thing it carries no information and is pure noise. It now appears once,
                    as the group's own framing, and only the EXCEPTION — a brand we do have
                    on file — is marked per chip.
                  */}
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    {suggestedCompanies.map(company => (
                      <CompanyChip
                        key={company.linkId}
                        company={company}
                        busy={companyBusyId === company.linkId || companyBusyId === ALL_SUGGESTED}
                        onConfirm={() => runCompanyAction('confirm', company.linkId)}
                        onDismiss={() => runCompanyAction('dismiss', company.linkId)}
                      />
                    ))}
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </Card>

        {/*
          VRG-573 — pull-quotes. A separate card from Claims because they are a different
          kind of thing: a claim is a measurement Ask Vera may cite, a quote is language
          chosen for how it is said and is only ever sent to a brand by a human. Filing
          them together would invite someone to treat an approved quote as market data.

          Hidden entirely when there are none AND there is nothing to extract from — an
          empty card explaining a feature that cannot run here is noise on the page.
        */}
        {(item.quotes.length > 0 || Boolean(item.bodyText)) && (
          <Card title="Quotes" spacing={4}>
            {item.quotes.length === 0 ? (
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                No quotes proposed yet. Extraction picks out sentences worth sending a brand
                verbatim, separately from the statistics it proposes as claims.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {item.quotes.map(quote => (
                  <Stack
                    key={quote.id}
                    direction="row"
                    alignItems="flex-start"
                    spacing={3}
                    sx={{ minWidth: 0 }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
                        &ldquo;{quote.text}&rdquo;
                      </Typography>
                      {quote.speaker && (
                        <Typography
                          variant="body4"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          {quote.speaker}
                        </Typography>
                      )}
                    </Box>
                    {quote.approvedAt ? (
                      <StatusChip
                        statuses={MARKET_STAT_STATUSES}
                        status="approved"
                        size="small"
                      />
                    ) : (
                      <Stack direction="row" spacing={3} sx={{ flexShrink: 0 }}>
                        <Button
                          variant="secondary"
                          onClick={() => runQuoteAction('approve', quote.id)}
                          loading={quoteBusyId === quote.id}
                        >
                          Approve
                        </Button>
                        <Button
                          variant="secondary"
                          destructive="hover"
                          onClick={() => runQuoteAction('dismiss', quote.id)}
                          loading={quoteBusyId === quote.id}
                        >
                          Dismiss
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Card>
        )}

        {/*
          STATS FOR THIS PIECE OF CONTENT, with Extract living here rather than only in
          the Content tab row's kebab.

          The kebab action was the only way to run extraction, and clicking it produced a
          one-line note above a table and nothing else — no indication of WHERE the
          results went, which made a billed model call feel like it had done nothing. Here
          the button sits beside the rows it produces, so running it has a visible effect
          in the same view.

          The kebab action stays: the Content tab is where you triage 26 rows, and making
          someone open each one to extract would be worse. Two entry points to one action
          is fine; one entry point with no visible outcome was not.
        */}
        <Card
          title="Claims"
          spacing={4}
          action={
            <Button
              variant="secondary"
              startIcon={<SparklesIcon strokeWidth={1} size={16} />}
              onClick={runExtract}
              loading={extractStats.isPending}
              // RECORD STATE, so hidden rather than disabled (§46): with no body text
              // there is nothing to extract from, and extraction is a BILLED call, so
              // offering it where it must return nothing spends money for no result.
              sx={{ display: item.bodyText ? undefined : 'none' }}
            >
              Extract claims
            </Button>
          }
        >
          {extractNote && (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, mb: 3 }}>
              {extractNote}
            </Typography>
          )}
          {itemStats.length === 0 ? (
            <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {item.bodyText
                ? 'No claims extracted yet. Extraction proposes claims from the article text; nothing reaches Ask Vera until a human approves it on the Claims tab.'
                : 'No body text was recovered for this page, so there is nothing to extract from. Paste the article text in by hand if you want statistics from it.'}
            </Typography>
          ) : (
            <Stack spacing={3}>
              {itemStats.map(stat => (
                <Stack
                  key={stat.id}
                  direction="row"
                  alignItems="flex-start"
                  spacing={3}
                  sx={{ minWidth: 0 }}
                >
                  <StatusChip
                    statuses={MARKET_STAT_STATUSES}
                    status={stat.approvedAt ? 'approved' : stat.rejectedAt ? 'rejected' : 'pending'}
                    size="small"
                  />
                  <Typography variant="body3" sx={{ flex: 1, minWidth: 0, lineHeight: 1.5 }}>
                    {stat.verbatimQuote}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>
      </Stack>

      <AssignMarketContentCompaniesModal
        itemId={item.id}
        // CONFIRMED and RESOLVED only. The modal sends a list of company ids, which is
        // precisely the set `setCompanies` owns — passing suggestions would pre-tick
        // boxes for links nobody has agreed to, and passing unmatched mentions would
        // pass rows that have no id to send.
        companies={confirmedCompanies}
        open={companiesModalOpen}
        onClose={() => setCompaniesModalOpen(false)}
      />
    </Box>
  )
}

/** A label + value pair for the read-only metadata row. */
/**
 * A read-only label/value pair, label ABOVE the value.
 *
 * `display: 'block'` on both is load-bearing, not decoration. `body3`/`body4` are custom
 * variants added by the app's Typography augmentation and are NOT in MUI's
 * `variantMapping`, so they fall back to rendering as `<span>` — inline. Inline, a short
 * label and its value pack onto the same line and read as "SourceLuxe Digital", while a
 * longer pair happens to wrap and looks correct, so the layout appeared to be
 * inconsistent between fields rather than uniformly wrong.
 */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Box>
    <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
      {label}
    </Typography>
    <Typography variant="body3" sx={{ display: 'block' }}>
      {children}
    </Typography>
  </Box>
)

/**
 * One company as a COMPACT CHIP.
 *
 * ## Why a chip and not a row
 *
 * A single vinyl article produced twenty mentions, which rendered as twenty full-width
 * rows: a column of near-identical lines scanned top to bottom, each repeating "Not a
 * company on file". Twenty of twenty saying the same thing is the tell — the label carried
 * no information and was the loudest text in the card. Chips wrap, so the same twenty fit
 * in four lines and read as a SET, which is what they are.
 *
 * The per-row label is gone. It is the group's framing now, and only the EXCEPTION — a
 * brand we actually have on file — is marked per chip, with its logo and a tooltip. That
 * inverts what was there: the rare, valuable case is what stands out, instead of the
 * common one being restated twenty times.
 *
 * ## Why the icons are what they are
 *
 * Dismiss is an `X`, never a trash can. It severs an association; the company record is
 * untouched and the model re-proposes on the next run, so a trash can would overstate a
 * cheap, reversible action (working-conventions §38). Neither control gets a confirmation
 * modal for the same reason — there is nothing to recover.
 */
const CompanyChip = ({
  company,
  busy,
  onConfirm,
  onDismiss,
}: {
  company: IMarketItemCompanyRef
  busy: boolean
  onConfirm?: () => void
  onDismiss: () => void
}) => {
  const theme = useTheme()
  const onFile = Boolean(company.companyId)
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{
        pl: 1,
        pr: 1,
        py: 1,
        borderRadius: '8px',
        border: `1px solid ${theme.palette.primary[100]}`,
        // A brand already on file is the exception worth seeing, so it carries a faint
        // brand tint. Deliberately a TINT and not a border: §34's rule is that a border
        // means "chosen", and these are all equally unchosen until confirmed.
        backgroundColor: onFile ? alpha(theme.palette.brand[500], 0.06) : 'transparent',
        opacity: busy ? 0.5 : 1,
        transition: 'opacity 120ms',
        maxWidth: '100%',
      }}
    >
      <Tooltip title={onFile ? 'Already a company on file' : 'Not a company on file — a prospect'}>
        <span style={{ display: 'flex' }}>
          <CompanyAvatar logo={company.logo} name={company.name} size={20} />
        </span>
      </Tooltip>
      <Typography variant="body3" noWrap sx={{ minWidth: 0 }}>
        {company.name}
      </Typography>
      {onConfirm && (
        <Tooltip
          title={
            company.confidence === 'normalised'
              ? 'Matched after normalising the name — check it is the right brand'
              : 'Confirm this content is about this company'
          }
        >
          <span>
            <IconButton size="small" onClick={onConfirm} disabled={busy} sx={{ p: 0.5 }}>
              <CheckIcon strokeWidth={1} size={15} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title={onConfirm ? 'Dismiss this suggestion' : 'Remove this company'}>
        <span>
          <IconButton size="small" onClick={onDismiss} disabled={busy} sx={{ p: 0.5 }}>
            <XIcon strokeWidth={1} size={15} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}
